import { coerceDateTime } from '../my-journeys/myJourneysModel';
import { snowflakeId } from '../journey-accommodation/accommodationModel';
import { BUDGET_CATEGORY_CODES } from '../finance/budgetCategory';
import {
  BUDGET_CURRENCY_OPTIONS,
  MONEY_SCALE,
  compareMoney,
  formatMoneyDisplay,
  moneyToCanonicalString,
  subtractMoneyStrings,
  sumMoneyStrings,
} from '../finance/financeMoney';

export {
  BUDGET_CATEGORY_CODES,
  BUDGET_CURRENCY_OPTIONS,
  MONEY_SCALE,
  compareMoney,
  formatMoneyDisplay,
  moneyToCanonicalString,
  sumMoneyStrings,
};

export function classifyBudgetError(cause) {
  const code = cause?.code ?? cause?.raw?.code;
  const key = String(cause?.raw?.key || cause?.message || '');
  if (code === 90003 || code === 90005 || code === 90006
      || key.includes('token.not.found') || key.includes('token.illegal') || key.includes('token.expired')) {
    return 'unauthorized';
  }
  if (code === 92001 || key === 'travel.journey.not.found') return 'journey-not-found';
  if (code === 93001 && key.includes('budget.not.found')) return 'budget-not-found';
  if (code === 93001 || key.includes('planning.not.found')) return 'not-found';
  if (code === 92002 || code === 93002 || key.includes('permission.denied')) return 'forbidden';
  if (code === 93003 && key.includes('concurrent.modification')) return 'conflict';
  if (code === 93003 && key.includes('currency.mismatch')) return 'currency-mismatch';
  if (code === 93003 && key.includes('already.exists')) return 'already-exists';
  if (code === 93003 && key.includes('lifecycle')) return 'lifecycle';
  if (code === 93003 || key.includes('concurrent')) return 'conflict';
  if (code === 92000 || code === 93000 || key.includes('validation')) return 'validation';
  return 'error';
}

export function normalizeBudgetCategory(value) {
  return {
    allocationId: snowflakeId(value?.allocationId),
    categoryCode: String(value?.categoryCode || ''),
    plannedAmount: moneyToCanonicalString(value?.plannedAmount) || '0.00000000',
    displayOrder: Number(value?.displayOrder) || 0,
  };
}

export function normalizeJourneyBudget(value) {
  const categories = Array.isArray(value?.categories)
    ? value.categories.map(normalizeBudgetCategory)
    : [];
  return {
    budgetId: snowflakeId(value?.budgetId),
    journeyId: snowflakeId(value?.journeyId),
    baseCurrency: String(value?.baseCurrency || '').toUpperCase(),
    baselineAmount: moneyToCanonicalString(value?.baselineAmount) || '0.00000000',
    allocatedAmount: moneyToCanonicalString(value?.allocatedAmount) || '0.00000000',
    unallocatedAmount: moneyToCanonicalString(value?.unallocatedAmount) || '0.00000000',
    categories,
    rowVersion: value?.rowVersion == null ? null : String(value.rowVersion),
    configuredAt: coerceDateTime(value?.configuredAt),
    configuredByMemberId: snowflakeId(value?.configuredByMemberId),
    createTime: coerceDateTime(value?.createTime),
    updateTime: coerceDateTime(value?.updateTime),
    noOp: value?.noOp == null ? null : Boolean(value.noOp),
  };
}

export function normalizeBudgetSummary(value) {
  return {
    journeyId: snowflakeId(value?.journeyId),
    budgetId: snowflakeId(value?.budgetId),
    baseCurrency: String(value?.baseCurrency || '').toUpperCase(),
    baselineAmount: moneyToCanonicalString(value?.baselineAmount) || '0.00000000',
    allocatedAmount: moneyToCanonicalString(value?.allocatedAmount) || '0.00000000',
    unallocatedAmount: moneyToCanonicalString(value?.unallocatedAmount) || '0.00000000',
    categoryCount: Number(value?.categoryCount) || 0,
    rowVersion: value?.rowVersion == null ? null : String(value.rowVersion),
    configuredAt: coerceDateTime(value?.configuredAt),
    configuredByMemberId: snowflakeId(value?.configuredByMemberId),
  };
}

export function emptyBudgetDraft(preferredCurrency = 'TWD') {
  const currency = String(preferredCurrency || 'TWD').toUpperCase();
  return {
    baseCurrency: BUDGET_CURRENCY_OPTIONS.includes(currency) ? currency : 'TWD',
    baselineAmount: '',
    categories: BUDGET_CATEGORY_CODES.map((categoryCode, index) => ({
      categoryCode,
      plannedAmount: '0',
      displayOrder: index + 1,
    })),
  };
}

export function draftFromBudget(budget) {
  const byCode = new Map((budget?.categories || []).map((row) => [row.categoryCode, row]));
  return {
    baseCurrency: budget?.baseCurrency || 'TWD',
    baselineAmount: formatMoneyDisplay(budget?.baselineAmount || '0'),
    categories: BUDGET_CATEGORY_CODES.map((categoryCode, index) => {
      const existing = byCode.get(categoryCode);
      return {
        categoryCode,
        plannedAmount: formatMoneyDisplay(existing?.plannedAmount || '0'),
        displayOrder: existing?.displayOrder || index + 1,
      };
    }),
  };
}

export function buildBudgetSetPayload(draft) {
  const baselineAmount = moneyToCanonicalString(draft?.baselineAmount);
  return {
    baseCurrency: String(draft?.baseCurrency || '').trim().toUpperCase(),
    baselineAmount,
    categories: (draft?.categories || []).map((row, index) => ({
      categoryCode: row.categoryCode,
      plannedAmount: moneyToCanonicalString(row.plannedAmount),
      displayOrder: Number(row.displayOrder) || index + 1,
    })),
    confirm: true,
  };
}

export function buildBudgetUpdatePayload(draft, expectedBudgetRowVersion) {
  return {
    expectedBudgetRowVersion: Number(expectedBudgetRowVersion),
    ...buildBudgetSetPayload(draft),
  };
}

/** 驗證結果代碼；文案由呈現層依目前語系翻譯，model 不持有任何語系文字。 */
export const BUDGET_VALIDATION_CODES = Object.freeze({
  CURRENCY_REQUIRED: 'CURRENCY_REQUIRED',
  BASELINE_INVALID: 'BASELINE_INVALID',
  BASELINE_NEGATIVE: 'BASELINE_NEGATIVE',
  CATEGORIES_REQUIRED: 'CATEGORIES_REQUIRED',
  CATEGORY_UNKNOWN: 'CATEGORY_UNKNOWN',
  CATEGORY_AMOUNT_INVALID: 'CATEGORY_AMOUNT_INVALID',
  CATEGORY_AMOUNT_NEGATIVE: 'CATEGORY_AMOUNT_NEGATIVE',
  ALLOCATION_EXCEEDS_TOTAL: 'ALLOCATION_EXCEEDS_TOTAL',
});

/** 通過回 null；失敗回 { code, categoryCode? }。 */
export function validateBudgetDraft(draft) {
  const V = BUDGET_VALIDATION_CODES;
  const currency = String(draft?.baseCurrency || '').trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(currency)) return { code: V.CURRENCY_REQUIRED };
  const baseline = moneyToCanonicalString(draft?.baselineAmount);
  if (!baseline) return { code: V.BASELINE_INVALID };
  if (baseline.startsWith('-')) return { code: V.BASELINE_NEGATIVE };
  const categories = draft?.categories || [];
  if (!categories.length) return { code: V.CATEGORIES_REQUIRED };
  let allocated = '0.00000000';
  for (const row of categories) {
    if (!BUDGET_CATEGORY_CODES.includes(row.categoryCode)) {
      return { code: V.CATEGORY_UNKNOWN, categoryCode: row.categoryCode };
    }
    const amount = moneyToCanonicalString(row.plannedAmount);
    if (!amount) return { code: V.CATEGORY_AMOUNT_INVALID, categoryCode: row.categoryCode };
    if (amount.startsWith('-')) {
      return { code: V.CATEGORY_AMOUNT_NEGATIVE, categoryCode: row.categoryCode };
    }
    allocated = sumMoneyStrings([allocated, amount]);
  }
  if (compareMoney(allocated, baseline) > 0) {
    return { code: V.ALLOCATION_EXCEEDS_TOTAL };
  }
  return null;
}

export function previewAllocated(draft) {
  return sumMoneyStrings((draft?.categories || []).map((row) => row.plannedAmount));
}

export function previewUnallocated(draft) {
  const baseline = moneyToCanonicalString(draft?.baselineAmount) || '0.00000000';
  const allocated = previewAllocated(draft);
  return subtractMoneyStrings(baseline, allocated);
}

export { snowflakeId };
